import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, LayoutTemplate } from "lucide-react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import clsx from "clsx";

import { PageHeader } from "../components/shared/PageHeader";
import { AdminTable } from "../components/shared/AdminTable";
import { SlideOver } from "../components/shared/SlideOver";
import { StatusBadge } from "../components/shared/StatusBadge";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { RichTextEditor } from "../components/shared/RichTextEditor";
import { usePages, useCreatePage, useUpdatePage, useDeletePage } from "../../../core/api/hooks/usePages";
import { ApiPage } from "../../../core/api/types";
import { useAdminT } from "../../../hooks/useAdminT";

const pageSchema = z.object({
    slug: z.string().min(1).regex(/^[a-z0-9-]+$/),
    published: z.boolean(),
    translations: z.array(z.object({
        locale: z.string(),
        title: z.string().min(1),
        content: z.string(),
        id: z.string().optional(),
    }))
});

type PageFormValues = z.infer<typeof pageSchema>;

export const PagesPage = () => {
    const { t } = useAdminT();
    const { data: pages, isLoading } = usePages();
    const createMutation = useCreatePage();
    const updateMutation = useUpdatePage();
    const deleteMutation = useDeletePage();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingPage, setEditingPage] = useState<ApiPage | null>(null);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [formLocale, setFormLocale] = useState<"en" | "ar">("en");

    const { control, register, handleSubmit, reset } = useForm<PageFormValues>({
        resolver: zodResolver(pageSchema),
        defaultValues: {
            slug: "",
            published: false,
            translations: [
                { locale: "en", title: "", content: "" },
                { locale: "ar", title: "", content: "" }
            ]
        }
    });

    const { fields: translationFields } = useFieldArray({ control, name: "translations" });

    useEffect(() => {
        if (editingPage) {
            const en = editingPage.translations.find(t => t.locale === "en") || { title: "", content: "" };
            const ar = editingPage.translations.find(t => t.locale === "ar") || { title: "", content: "" };
            reset({
                slug: editingPage.slug,
                published: editingPage.published,
                translations: [
                    { locale: "en", title: en.title, content: en.content },
                    { locale: "ar", title: ar.title, content: ar.content }
                ]
            });
        } else {
            reset({
                slug: "",
                published: false,
                translations: [
                    { locale: "en", title: "", content: "" },
                    { locale: "ar", title: "", content: "" }
                ]
            });
        }
    }, [editingPage, reset]);

    const onSubmit = (data: PageFormValues) => {
        if (editingPage) {
            updateMutation.mutate({ id: editingPage.id, ...data } as any, {
                onSuccess: () => setIsDrawerOpen(false)
            });
        } else {
            createMutation.mutate(data as any, {
                onSuccess: () => setIsDrawerOpen(false)
            });
        }
    };

    const columns = [
        {
            header: t("pages.fields.pageInfo"),
            accessorKey: "slug" as keyof ApiPage,
            cell: (item: ApiPage) => (
                <div>
                    <div className="font-medium text-slate-900">{item.translations?.[0]?.title || t("pages.untitled")}</div>
                    <div className="text-xs text-slate-500">/{item.slug || ""}</div>
                </div>
            )
        },
        {
            header: t("common.status"),
            accessorKey: "published" as keyof ApiPage,
            cell: (item: ApiPage) => <StatusBadge status={item.published ? "published" : "draft"} />
        },
        {
            header: t("common.updatedAt"),
            accessorKey: "updatedAt" as keyof ApiPage,
            cell: (item: ApiPage) => {
                const date = item.updatedAt ? new Date(item.updatedAt) : null;
                return date && !isNaN(date.getTime()) ? date.toLocaleDateString() : "-";
            }
        }
    ];

    const actions = (row: ApiPage) => (
        <div className="flex gap-2">
            <Link
                to={`/admin/cms/pages/${row.id}/builder`}
                className="p-1 text-slate-400 hover:text-primary transition-colors"
                title={t("pages.pageBuilder")}
            >
                <LayoutTemplate size={16} />
            </Link>
            <button
                onClick={() => { setEditingPage(row); setIsDrawerOpen(true); }}
                className="p-1 text-slate-400 hover:text-blue-600 transition-colors"
                title={t("common.edit")}
            >
                <Edit2 size={16} />
            </button>
            <button
                onClick={() => setDeleteId(row.id)}
                className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                title={t("common.delete")}
            >
                <Trash2 size={16} />
            </button>
        </div>
    );

    return (
        <div className="space-y-6 p-8">
            <PageHeader
                title={t("pages.title")}
                description={t("pages.subtitle")}
                actions={[{
                    label: t("pages.add"),
                    icon: Plus,
                    onClick: () => { setEditingPage(null); setIsDrawerOpen(true); }
                }]}
            />

            <AdminTable
                data={Array.isArray(pages) ? pages : []}
                columns={columns}
                isLoading={isLoading}
                actions={actions}
                searchPlaceholder={t("common.search")}
            />

            <SlideOver
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                title={editingPage ? t("pages.edit") : t("pages.add")}
                wide={true}
                footer={
                    <>
                        <button
                            type="button"
                            onClick={() => setIsDrawerOpen(false)}
                            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors border border-slate-300"
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            onClick={handleSubmit(onSubmit)}
                            className="px-4 py-2 text-sm font-medium text-white bg-secondary hover:bg-secondary/90 rounded-lg transition-colors shadow-sm"
                        >
                            {editingPage ? t("pages.update") : t("pages.create")}
                        </button>
                    </>
                }
            >
                <form className="space-y-6 p-1">
                    {/* Common Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t("pages.fields.slug")}</label>
                            <input
                                {...register("slug")}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all"
                                placeholder="e.g. about-us"
                            />
                        </div>
                        <div className="flex items-center pt-6">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    {...register("published")}
                                    className="w-4 h-4 text-secondary rounded border-slate-300 focus:ring-secondary"
                                />
                                <span className="text-sm font-medium text-slate-700">{t("common.published")}</span>
                            </label>
                        </div>
                    </div>

                    <hr className="border-slate-200" />

                    {/* Locale Switcher */}
                    <div className="flex gap-2 bg-slate-100 p-1 rounded-lg w-fit">
                        <button
                            type="button"
                            onClick={() => setFormLocale("en")}
                            className={clsx(
                                "px-4 py-1.5 rounded-md text-sm font-medium transition-all",
                                formLocale === "en" ? "bg-white shadow text-secondary" : "text-slate-500 hover:text-slate-700"
                            )}
                        >
                            {t("common.locale.en")}
                        </button>
                        <button
                            type="button"
                            onClick={() => setFormLocale("ar")}
                            className={clsx(
                                "px-4 py-1.5 rounded-md text-sm font-medium transition-all",
                                formLocale === "ar" ? "bg-white shadow text-secondary" : "text-slate-500 hover:text-slate-700"
                            )}
                        >
                            {t("common.locale.ar")}
                        </button>
                    </div>

                    {/* Translatable Fields */}
                    <div dir={formLocale === "ar" ? "rtl" : "ltr"}>
                        {translationFields.map((field, index) => {
                            const isHidden = (index === 0 && formLocale !== 'en') || (index === 1 && formLocale !== 'ar');
                            return (
                                <div key={field.id} className={clsx("space-y-4", isHidden && "hidden")}>
                                    <input type="hidden" {...register(`translations.${index}.locale`)} />
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            {t("pages.fields.title")}
                                        </label>
                                        <input
                                            {...register(`translations.${index}.title`)}
                                            type="text"
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            {t("pages.fields.content")}
                                        </label>
                                        <Controller
                                            name={`translations.${index}.content`}
                                            control={control}
                                            render={({ field }) => (
                                                <RichTextEditor
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    placeholder={index === 0 ? "Start typing..." : "ابدأ الكتابة..."}
                                                    dir={index === 1 ? 'rtl' : 'ltr'}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </form>
            </SlideOver>

            <ConfirmDialog
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={() => {
                    if (deleteId) deleteMutation.mutate(deleteId);
                    setDeleteId(null);
                }}
                title={t("pages.deleteConfirm")}
                message={t("common.confirmProceed")}
                variant="danger"
            />
        </div>
    );
};
